import { useMemo, useState } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'
import clsx from 'clsx'
import {
  FACET_FILTERS,
  ltvBoundFromTyped,
  ltvBoundPercent,
  type DealSearchSchema,
  type Facets,
  type SearchFilters,
} from './types'
import { count } from './format'

/** Multi-select facet group. Collapsed by default beyond the first few so the
 *  rail stays scannable; every option carries its corpus count so an operator
 *  can see how selective a choice is before making it. */
function FacetGroup({
  label,
  facet,
  facets,
  selected,
  onChange,
}: {
  label: string
  facet: string
  facets: Facets | undefined
  selected: string[]
  onChange: (values: string[]) => void
}) {
  const [open, setOpen] = useState(selected.length > 0)
  const [filter, setFilter] = useState('')
  const entry = facets?.facets?.[facet]

  const visible = useMemo(() => {
    const values = entry?.values ?? []
    if (!filter.trim()) return values
    const needle = filter.trim().toLowerCase()
    return values.filter((v) => (v.label ?? v.value).toLowerCase().includes(needle))
  }, [entry, filter])

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  return (
    <div className="border-b border-amber-300/[0.07]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition hover:bg-white/[0.02]"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400">
          {label}
        </span>
        <span className="flex items-center gap-1.5">
          {selected.length > 0 && (
            <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-mono text-amber-200">
              {selected.length}
            </span>
          )}
          <ChevronDown
            size={12}
            className={clsx('text-stone-600 transition-transform', open && 'rotate-180')}
          />
        </span>
      </button>

      {open && (
        <div className="px-3 pb-2.5">
          {/* An unrecorded dimension says so. It never renders an empty
              dropdown that reads as a loading failure, and it never invents
              placeholder options. */}
          {!entry ? (
            <p className="py-1 text-[10px] text-stone-600">Loading…</p>
          ) : !entry.recorded ? (
            <p className="py-1 text-[10px] leading-relaxed text-stone-600">
              No values recorded on any deal yet.
            </p>
          ) : (
            <>
              {entry.values.length > 8 && (
                <input
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder={`Find ${label.toLowerCase()}…`}
                  aria-label={`Filter ${label} options`}
                  className="mb-1.5 w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1 text-[10px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
                />
              )}
              <ul className="max-h-44 space-y-0.5 overflow-y-auto">
                {visible.map((option) => {
                  const checked = selected.includes(option.value)
                  return (
                    <li key={option.value}>
                      <label
                        className={clsx(
                          'flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] transition',
                          checked ? 'bg-amber-400/[0.08] text-stone-100' : 'text-stone-400 hover:bg-white/[0.02]',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(option.value)}
                          className="h-3 w-3 accent-amber-400"
                        />
                        <span className="flex-1 truncate" title={option.label ?? option.value}>
                          {option.label ?? option.value}
                        </span>
                        <span className="font-mono text-[9px] text-stone-600">{count(option.count)}</span>
                      </label>
                    </li>
                  )
                })}
                {visible.length === 0 && (
                  <li className="px-1.5 py-1 text-[10px] text-stone-600">No match.</li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const same = (value: number) => value

/** A min–max pair. `toShown` and `fromTyped` convert between the filter value
 *  and what the inputs show: the LTV is typed as a percent (60) but the
 *  column and the API hold the fraction (0.60). The rail used to send the
 *  typed 60 as-is, and every LTV range matched no deal (R1). */
function NumberRange({
  label,
  suffix,
  min,
  max,
  step,
  toShown = same,
  fromTyped = same,
  onChange,
}: {
  label: string
  suffix?: string
  min: number | null
  max: number | null
  step?: string
  toShown?: (value: number) => number
  fromTyped?: (typed: number) => number
  onChange: (next: { min: number | null; max: number | null }) => void
}) {
  const parse = (raw: string) => {
    if (raw.trim() === '') return null
    const typed = Number(raw)
    return Number.isFinite(typed) ? fromTyped(typed) : null
  }
  // Rounded so 0.58 reads back as 58, not 57.99999999999999. Kept a NUMBER:
  // React leaves a number field alone while its text still equals the value
  // numerically ("1.0" == 1), but compares a string value as text, so a
  // String here rewrote "1.0" to "1" and the next key made 1.05 into 15.
  const shown = (value: number | null) =>
    value === null ? '' : Number(toShown(value).toFixed(6))
  return (
    <div className="px-3 py-2">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400">
        {label}
        {suffix ? <span className="ml-1 text-stone-600">{suffix}</span> : null}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          step={step}
          value={shown(min)}
          onChange={(event) => onChange({ min: parse(event.target.value), max })}
          placeholder="Min"
          aria-label={`${label} minimum`}
          className="w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1 text-[10px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
        />
        <span className="text-[10px] text-stone-600">–</span>
        <input
          type="number"
          step={step}
          value={shown(max)}
          onChange={(event) => onChange({ min, max: parse(event.target.value) })}
          placeholder="Max"
          aria-label={`${label} maximum`}
          className="w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1 text-[10px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
        />
      </div>
    </div>
  )
}

/** The date fields a window can run on, in the server's words. The schema
 *  sends them; these stand in while it loads so the control never renders
 *  empty. */
const DEFAULT_DATE_FIELDS = [
  { key: 'created_at', label: 'Entered' },
  { key: 'funded_at', label: 'Closed' },
  { key: 'dead_at', label: 'Marked dead' },
]

/** Any From / To window on any date the API filters by (requirement 1.2).
 *  The toolbar's presets cover the standard periods; "1 Mar – 15 Jun" had no
 *  way in (R3). A preset wins over explicit dates on the server, so touching
 *  this range drops the preset rather than leaving a window that does
 *  nothing. */
function DateRange({
  filters,
  schema,
  onPatch,
}: {
  filters: SearchFilters
  schema: DealSearchSchema | undefined
  onPatch: (patch: Partial<SearchFilters>) => void
}) {
  const fields = schema?.dateFields?.length ? schema.dateFields : DEFAULT_DATE_FIELDS
  // `min-w-0` and the minmax(0, 1fr) tracks below let the pair shrink to the
  // rail. A date input will not go below its min-content width (107px at 10px
  // Inter) and a bare `1fr` track honours it, so the To input ran 16px past
  // the 228px rail and the rail scrolled sideways.
  const inputClass =
    'w-full min-w-0 rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 [color-scheme:dark] focus:border-amber-300/30 focus:outline-none'
  return (
    <div className="border-b border-amber-300/[0.07] px-3 py-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400">
          Date range
        </span>
        <select
          value={filters.dateField}
          onChange={(event) => onPatch({ dateField: event.target.value, datePreset: null })}
          aria-label="Date range field"
          className="rounded border border-amber-300/10 bg-black/40 px-1 py-0.5 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          {fields.map((field) => (
            <option key={field.key} value={field.key}>
              {field.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
        <input
          type="date"
          value={filters.dateFrom ?? ''}
          max={filters.dateTo ?? undefined}
          onChange={(event) => onPatch({ dateFrom: event.target.value || null, datePreset: null })}
          aria-label="Date range from"
          className={inputClass}
        />
        <span className="text-[10px] text-stone-600">–</span>
        <input
          type="date"
          value={filters.dateTo ?? ''}
          min={filters.dateFrom ?? undefined}
          onChange={(event) => onPatch({ dateTo: event.target.value || null, datePreset: null })}
          aria-label="Date range to"
          className={inputClass}
        />
      </div>
      {filters.datePreset ? (
        <p className="mt-1 text-[9px] leading-relaxed text-stone-600">
          A period is chosen above; entering dates here replaces it.
        </p>
      ) : null}
    </div>
  )
}

export function FilterRail({
  filters,
  schema,
  facets,
  onPatch,
  onReset,
  activeCount,
}: {
  filters: SearchFilters
  schema: DealSearchSchema | undefined
  facets: Facets | undefined
  onPatch: (patch: Partial<SearchFilters>) => void
  onReset: () => void
  activeCount: number
}) {
  const stages = schema?.stages ?? []

  return (
    <aside
      aria-label="Deal filters"
      className="flex w-[228px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-amber-300/10 bg-[#0b0b0b]"
    >
      <div className="flex items-center justify-between border-b border-amber-300/10 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          Filters
        </span>
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-stone-500 transition enabled:hover:text-amber-200 disabled:opacity-40"
        >
          <X size={10} /> Clear{activeCount ? ` (${activeCount})` : ''}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <DateRange filters={filters} schema={schema} onPatch={onPatch} />

        {/* Stage range — the "from X through Y" the requirement calls for,
            expressed as two ordered selects rather than a checkbox soup. */}
        <div className="border-b border-amber-300/[0.07] px-3 py-2">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400">
            Stage range
          </div>
          <div className="flex items-center gap-1.5">
            <select
              value={filters.stageFrom ?? ''}
              onChange={(event) => onPatch({ stageFrom: event.target.value || null })}
              aria-label="Stage range start"
              className="w-full rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
            >
              <option value="">First</option>
              {stages.map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-stone-600">→</span>
            <select
              value={filters.stageTo ?? ''}
              onChange={(event) => onPatch({ stageTo: event.target.value || null })}
              aria-label="Stage range end"
              className="w-full rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
            >
              <option value="">Last</option>
              {stages.map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
          </div>
        </div>

        {FACET_FILTERS.map(({ facet, filterKey, label }) => (
          <FacetGroup
            key={facet}
            label={label}
            facet={facet}
            facets={facets}
            selected={(filters[filterKey] as string[]) ?? []}
            onChange={(values) => onPatch({ [filterKey]: values } as Partial<SearchFilters>)}
          />
        ))}

        <div className="border-b border-amber-300/[0.07]">
          <NumberRange
            label="Loan amount"
            suffix="$"
            step="100000"
            min={filters.amountMin}
            max={filters.amountMax}
            onChange={({ min, max }) => onPatch({ amountMin: min, amountMax: max })}
          />
        </div>
        <div className="border-b border-amber-300/[0.07]">
          <NumberRange
            label="LTV"
            suffix="%"
            step="1"
            toShown={ltvBoundPercent}
            fromTyped={ltvBoundFromTyped}
            min={filters.ltvMin}
            max={filters.ltvMax}
            onChange={({ min, max }) => onPatch({ ltvMin: min, ltvMax: max })}
          />
        </div>
        <NumberRange
          label="DSCR"
          step="0.05"
          min={filters.dscrMin}
          max={filters.dscrMax}
          onChange={({ min, max }) => onPatch({ dscrMin: min, dscrMax: max })}
        />
      </div>

      {facets && (
        <div className="border-t border-amber-300/10 px-3 py-2 text-[9px] leading-relaxed text-stone-600">
          <Search size={9} className="mr-1 inline" />
          Searching {count(facets.corpus.totalDeals)} deals
          {facets.corpus.earliestReceived
            ? ` since ${facets.corpus.earliestReceived.slice(0, 4)}`
            : ''}
          {facets.corpus.deadDeals > 0 ? ` · ${count(facets.corpus.deadDeals)} dead` : ''}
        </div>
      )}
    </aside>
  )
}
